import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PTCard from './PTCard';
import PTDetailModal from './PTDetailModal';
import TVCarousel from './TVCarousel';
import CommentsModal from './CommentsModal';
import { RefreshCw, Bell, Monitor } from 'lucide-react';
import { PT, DashboardSummary } from './types';


// API URL: otomatis pakai hostname yang sama dengan browser (support akses dari TV via jaringan)
const API_BASE = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:8000/api`;

const MonitoringDashboard: React.FC = () => {
  const [pts, setPts] = useState<PT[]>([]);
  const [summary, setSummary] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<string>('');
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  const [selectedPT, setSelectedPT] = useState<PT | null>(null);
  const [commentsPT, setCommentsPT] = useState<PT | null>(null);
  const [tvMode, setTVMode] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/monitoring/dashboard`);
      const result = await response.json();

      if (result.success) {
        // Sort PTs by progress (worst first)
        const sortedPTs = result.data.sort(
          (a: PT, b: PT) => a.progress_percentage - b.progress_percentage
        );
        
        setPts(sortedPTs);
        setSummary(result.summary);
        setLastUpdate(new Date().toLocaleString('id-ID', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit'
        }));
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData();

    // Auto-refresh every 30 minutes
    const interval = setInterval(fetchData, 30 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && tvMode) {
        setTVMode(false);
      }
      if (e.key === 't' && e.ctrlKey) {
        e.preventDefault();
        setTVMode(!tvMode);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [tvMode]);

  const handleManualRefresh = async () => {
    setIsRefreshing(true);

    try {
      const syncResponse = await fetch(`${API_BASE}/monitoring/sync`, {
        method: 'POST',
      });

      if (syncResponse.ok) {
        console.log('Sync completed successfully');
        setTimeout(() => {
          fetchData();
        }, 2000);
      } else {
        console.error('Sync failed');
        fetchData();
      }
    } catch (error) {
      console.error('Sync error:', error);
      fetchData();
    }
  };

  const handleViewDetails = (pt: PT) => {
    setSelectedPT(pt);
  };

  const handleCloseModal = () => {
    setSelectedPT(null);
  };

  const handleViewComments = (pt: PT) => {
    setCommentsPT(pt);
  };

  const handleCloseComments = () => {
    setCommentsPT(null);
  };

  const handleEnterTVMode = () => {
    setTVMode(true);
  };

  const handleExitTVMode = () => {
    setTVMode(false);
  };

  // Calculate deadline info - all PTs have same deadline: 30 Juni
  const deadlineInfo = useMemo(() => {
    const today = new Date();
    const currentYear = today.getFullYear();

    // Deadline 30 Juni - pilih tahun ini atau tahun depan (mana yang belum lewat)
    let deadline = new Date(currentYear, 5, 30); // Juni = 5 (0-indexed)
    if (deadline < today) {
      // Jika 30 Juni tahun ini sudah lewat, pakai tahun depan
      deadline = new Date(currentYear + 1, 5, 30);
    }

    const timeDiff = deadline.getTime() - today.getTime();
    const daysRemaining = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));

    // Count incomplete PTs
    const incompletePTs = pts.filter(pt => pt.progress_percentage < 100).length;
    const completedPTs = pts.filter(pt => pt.progress_percentage >= 100).length;

    return {
      deadline,
      daysRemaining,
      incompletePTs,
      completedPTs,
      totalPTs: pts.length
    };
  }, [pts]);

  // Calculate team leader stats (3 main team leaders)
  const teamLeaderStats = useMemo(() => {
    const mainLeaders = ['Nabusfanando', 'Lativa Nurunnisa', 'Weni Eka Putri'];

    return mainLeaders.map(leaderName => {
      // Find all PTs where this leader is in team_members
      const leaderPTs = pts.filter(pt =>
        pt.team_members?.some(m =>
          m.name.toLowerCase().includes(leaderName.toLowerCase())
        )
      );

      const totalPT = leaderPTs.length;
      const completedPT = leaderPTs.filter(pt => pt.progress_percentage >= 100).length;
      const activePT = totalPT - completedPT;
      const avgProgress = totalPT > 0
        ? leaderPTs.reduce((sum, pt) => sum + pt.progress_percentage, 0) / totalPT
        : 0;

      return {
        name: leaderName,
        totalPT,
        completedPT,
        activePT,
        avgProgress,
        pts: leaderPTs
      };
    });
  }, [pts]);

  // TV Mode View
  if (tvMode) {
    return (
      <TVCarousel
        pts={pts}
        onExitTVMode={handleExitTVMode}
        onViewDetails={handleViewDetails}
      />
    );
  }

  // Desktop Mode View
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-white border-t-transparent mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900">
      {/* News Ticker / Marquee - Deadline Info */}
      <div className="bg-gradient-to-r from-blue-950 to-blue-900 border-b border-blue-700 overflow-hidden">
        <div className="py-2 flex items-center">
          <div className="bg-yellow-500 text-blue-900 px-4 py-1 text-sm font-bold flex-shrink-0 z-10">
            📢 INFO
          </div>
          <div className="overflow-hidden flex-1 relative">
            <div className="animate-marquee whitespace-nowrap inline-block">
              {/* Deadline Info Items */}
              <span className="inline-flex items-center mx-8">
                <span className="text-yellow-400 font-semibold">🎯 Deadline Audit:</span>
                <span className="text-blue-200 ml-2">
                  {deadlineInfo.deadline.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </span>
              <span className="text-blue-400 mx-4">•</span>

              <span className="inline-flex items-center mx-8">
                <span className="text-white">⏳ Sisa Waktu:</span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                  deadlineInfo.daysRemaining <= 30 ? 'bg-red-500 text-white' :
                  deadlineInfo.daysRemaining <= 60 ? 'bg-yellow-500 text-black' :
                  'bg-green-500 text-white'
                }`}>
                  {deadlineInfo.daysRemaining} hari lagi
                </span>
              </span>
              <span className="text-blue-400 mx-4">•</span>

              <span className="inline-flex items-center mx-8">
                <span className="text-white">⚠️ Belum Selesai:</span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                  deadlineInfo.incompletePTs > 10 ? 'bg-red-500 text-white' :
                  deadlineInfo.incompletePTs > 5 ? 'bg-yellow-500 text-black' :
                  'bg-green-500 text-white'
                }`}>
                  {deadlineInfo.incompletePTs} PT
                </span>
              </span>
              <span className="text-blue-400 mx-4">•</span>

              <span className="inline-flex items-center mx-8">
                <span className="text-emerald-400 font-semibold">✅ PT Selesai:</span>
                <span className="bg-emerald-600 text-white px-2 py-0.5 rounded-full text-xs font-bold ml-2">
                  {deadlineInfo.completedPTs} / {deadlineInfo.totalPTs} PT
                </span>
              </span>

              {/* Duplicate for seamless loop */}
              <span className="text-blue-400 mx-4">•</span>
              <span className="inline-flex items-center mx-8">
                <span className="text-yellow-400 font-semibold">🎯 Deadline Audit:</span>
                <span className="text-blue-200 ml-2">
                  {deadlineInfo.deadline.toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              </span>
              <span className="text-blue-400 mx-4">•</span>

              <span className="inline-flex items-center mx-8">
                <span className="text-white">⏳ Sisa Waktu:</span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                  deadlineInfo.daysRemaining <= 30 ? 'bg-red-500 text-white' :
                  deadlineInfo.daysRemaining <= 60 ? 'bg-yellow-500 text-black' :
                  'bg-green-500 text-white'
                }`}>
                  {deadlineInfo.daysRemaining} hari lagi
                </span>
              </span>
              <span className="text-blue-400 mx-4">•</span>

              <span className="inline-flex items-center mx-8">
                <span className="text-white">⚠️ Belum Selesai:</span>
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                  deadlineInfo.incompletePTs > 10 ? 'bg-red-500 text-white' :
                  deadlineInfo.incompletePTs > 5 ? 'bg-yellow-500 text-black' :
                  'bg-green-500 text-white'
                }`}>
                  {deadlineInfo.incompletePTs} PT
                </span>
              </span>
              <span className="text-blue-400 mx-4">•</span>

              <span className="inline-flex items-center mx-8">
                <span className="text-emerald-400 font-semibold">✅ PT Selesai:</span>
                <span className="bg-emerald-600 text-white px-2 py-0.5 rounded-full text-xs font-bold ml-2">
                  {deadlineInfo.completedPTs} / {deadlineInfo.totalPTs} PT
                </span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="flex justify-between items-start">
          <div className="flex items-center gap-4">
            <div className="bg-white bg-opacity-10 p-2 rounded-lg">
              <img
                src="/logo.png"
                alt="Logo"
                className="w-10 h-10 object-contain"
                onError={(e) => {
                  // Fallback to favicon.ico if logo.png fails
                  const target = e.target as HTMLImageElement;
                  if (!target.src.includes('favicon.ico')) {
                    target.src = '/favicon.ico';
                  }
                }}
              />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white mb-1">
                Auditra Monitoring Dashboard
              </h1>
              <p className="text-blue-200 text-sm">
                Last Update: {lastUpdate}
              </p>
              <p className="text-blue-300 text-xs">
                {currentTime.toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} - {currentTime.toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </p>
            </div>
          </div>

          <div className="flex gap-3">
            <button
              onClick={() => {/* Implement alerts panel */}}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 transition shadow-lg"
            >
              <Bell size={20} />
              <span>Alerts</span>
              {summary && summary.critical_pts > 0 && (
                <span className="bg-red-800 px-2 py-0.5 rounded-full text-xs font-bold">
                  {summary.critical_pts}
                </span>
              )}
            </button>

            <button
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 transition shadow-lg disabled:opacity-50"
            >
              <RefreshCw size={20} className={isRefreshing ? 'animate-spin' : ''} />
              <span>Sync Now</span>
            </button>

            <button
              onClick={handleEnterTVMode}
              className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-3 rounded-lg flex items-center gap-2 transition shadow-lg"
              title="Enter TV Mode (Ctrl+T)"
            >
              <Monitor size={20} />
              <span>TV Mode</span>
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="max-w-7xl mx-auto mb-6 grid grid-cols-5 gap-4">
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4 border border-white border-opacity-20">
            <p className="text-blue-200 text-sm mb-1">Total PTs</p>
            <p className="text-3xl font-bold text-white">{summary.total_pts}</p>
          </div>
          <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg p-4 border border-white border-opacity-20">
            <p className="text-blue-200 text-sm mb-1">Avg Progress</p>
            <p className="text-3xl font-bold text-white">{summary.average_progress.toFixed(1)}%</p>
          </div>
          <div className="bg-red-500 bg-opacity-20 backdrop-blur-sm rounded-lg p-4 border border-red-400 border-opacity-30">
            <p className="text-red-200 text-sm mb-1 flex items-center gap-1">
              <span className="text-lg">⚠️</span> Critical
            </p>
            <p className="text-3xl font-bold text-white">{summary.critical_pts}</p>
          </div>
          <div className="bg-yellow-500 bg-opacity-20 backdrop-blur-sm rounded-lg p-4 border border-yellow-400 border-opacity-30">
            <p className="text-yellow-200 text-sm mb-1 flex items-center gap-1">
              <span className="text-lg">⚠️</span> Warning
            </p>
            <p className="text-3xl font-bold text-white">{summary.warning_pts}</p>
          </div>
          <div className="bg-green-500 bg-opacity-20 backdrop-blur-sm rounded-lg p-4 border border-green-400 border-opacity-30">
            <p className="text-green-200 text-sm mb-1 flex items-center gap-1">
              <span className="text-lg">✓</span> Good
            </p>
            <p className="text-3xl font-bold text-white">{summary.good_pts}</p>
          </div>
        </div>
      )}

      {/* Team Leader Summary Table */}
      <div className="max-w-7xl mx-auto mb-6">
        <div className="bg-white bg-opacity-10 backdrop-blur-sm rounded-lg border border-white border-opacity-20 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3">
            <h2 className="text-white font-bold flex items-center gap-2">
              <span className="text-xl">👥</span> Team Leader Summary
            </h2>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-3 gap-4">
              {teamLeaderStats.map((leader, index) => {
                const colors = [
                  { bg: 'from-blue-500 to-blue-600', light: 'bg-blue-400', text: 'text-blue-100' },
                  { bg: 'from-emerald-500 to-emerald-600', light: 'bg-emerald-400', text: 'text-emerald-100' },
                  { bg: 'from-amber-500 to-amber-600', light: 'bg-amber-400', text: 'text-amber-100' }
                ];
                const color = colors[index % colors.length];

                // Real photo avatars
                const avatarMap: Record<string, string> = {
                  'Nabusfanando': '/nando.jpeg',
                  'Lativa Nurunnisa': '/latifah.jpeg',
                  'Weni Eka Putri': '/weni.jpeg',
                };
                const avatarUrl = avatarMap[leader.name] || `https://api.dicebear.com/7.x/adventurer-neutral/svg?seed=${encodeURIComponent(leader.name)}&backgroundColor=transparent`;

                return (
                  <div key={leader.name} className={`bg-gradient-to-br ${color.bg} rounded-lg p-4 shadow-lg`}>
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`${color.light} bg-opacity-30 rounded-full p-1 overflow-hidden flex-shrink-0`}>
                        <img
                          src={avatarUrl}
                          alt={leader.name}
                          className="w-14 h-14 rounded-full object-cover object-top"
                        />
                      </div>
                      <div>
                        <h3 className="text-white font-bold text-lg">{leader.name}</h3>
                        <p className={`${color.text} text-xs`}>Team Leader</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 mb-3">
                      <div className="bg-white bg-opacity-20 rounded-lg p-2 text-center">
                        <p className={`${color.text} text-xs`}>Total PT</p>
                        <p className="text-white text-2xl font-bold">{leader.totalPT}</p>
                      </div>
                      <div className="bg-white bg-opacity-20 rounded-lg p-2 text-center">
                        <p className={`${color.text} text-xs`}>Aktif</p>
                        <p className="text-white text-2xl font-bold">{leader.activePT}</p>
                      </div>
                      <div className="bg-white bg-opacity-20 rounded-lg p-2 text-center">
                        <p className={`${color.text} text-xs`}>Selesai</p>
                        <p className="text-white text-2xl font-bold">{leader.completedPT}</p>
                      </div>
                    </div>

                    <div className="bg-white bg-opacity-20 rounded-lg p-2 mb-2">
                      <div className="flex justify-between items-center mb-1">
                        <span className={`${color.text} text-xs`}>Avg Progress</span>
                        <span className="text-white font-bold">{leader.avgProgress.toFixed(1)}%</span>
                      </div>
                      <div className="w-full bg-white bg-opacity-30 rounded-full h-2">
                        <div
                          className="bg-white h-2 rounded-full transition-all"
                          style={{ width: `${Math.min(leader.avgProgress, 100)}%` }}
                        />
                      </div>
                    </div>

                    {/* PT List Ticker */}
                    {leader.pts.length > 0 && (
                      <div className="rounded-lg overflow-hidden mt-2" style={{ background: 'rgba(0,0,0,0.18)' }}>

                        {/* Row: Semua PT */}
                        <div className="flex items-center border-b border-white border-opacity-10" style={{ minHeight: '30px' }}>
                          <div className="bg-white bg-opacity-20 px-3 self-stretch flex items-center flex-shrink-0">
                            <span className="text-white text-xs font-semibold tracking-widest uppercase">Semua</span>
                          </div>
                          <div className="w-px self-stretch bg-white bg-opacity-20 flex-shrink-0" />
                          <div className="overflow-hidden flex-1">
                            <div className="leader-ticker whitespace-nowrap inline-flex items-center"
                              style={{ animation: `leaderScroll ${Math.max(leader.pts.length * 5, 15)}s linear infinite` }}>
                              {[...leader.pts, ...leader.pts].map((pt, i) => (
                                <span key={i} className="inline-flex items-center gap-2 px-4">
                                  <span className="w-1.5 h-1.5 rounded-full bg-white opacity-70 flex-shrink-0" />
                                  <span className="text-white text-xs font-medium">
                                    {pt.name.length > 30 ? pt.name.substring(0, 30) + '…' : pt.name}
                                  </span>
                                  <span className={`text-xs px-1.5 py-0.5 rounded font-bold ${
                                    pt.progress_percentage >= 100 ? 'bg-green-300 text-green-900' :
                                    pt.progress_percentage >= 80  ? 'bg-green-300 text-green-900' :
                                    pt.progress_percentage >= 50  ? 'bg-yellow-300 text-yellow-900' :
                                    pt.progress_percentage >= 20  ? 'bg-orange-300 text-orange-900' :
                                    'bg-red-300 text-red-900'
                                  }`}>
                                    {pt.progress_percentage.toFixed(0)}%
                                  </span>
                                  <span className="text-white opacity-20 text-xs mx-1">·</span>
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>

                        {/* Row: Aktif */}
                        {leader.pts.filter(p => p.progress_percentage < 100).length > 0 && (
                          <div className="flex items-center border-b border-white border-opacity-10" style={{ minHeight: '30px' }}>
                            <div className="bg-white bg-opacity-10 px-3 self-stretch flex items-center flex-shrink-0">
                              <span className="text-yellow-200 text-xs font-semibold tracking-widest uppercase">Aktif</span>
                            </div>
                            <div className="w-px self-stretch bg-white bg-opacity-20 flex-shrink-0" />
                            <div className="overflow-hidden flex-1">
                              <div className="leader-ticker whitespace-nowrap inline-flex items-center"
                                style={{ animation: `leaderScroll ${Math.max(leader.pts.filter(p => p.progress_percentage < 100).length * 5, 15)}s linear infinite` }}>
                                {[...leader.pts.filter(p => p.progress_percentage < 100), ...leader.pts.filter(p => p.progress_percentage < 100)].map((pt, i) => (
                                  <span key={i} className="inline-flex items-center gap-2 px-4">
                                    <span className="w-1.5 h-1.5 rounded-full bg-yellow-300 flex-shrink-0" />
                                    <span className="text-white text-xs font-medium">
                                      {pt.name.length > 30 ? pt.name.substring(0, 30) + '…' : pt.name}
                                    </span>
                                    <span className={`text-xs px-1.5 py-0.5 rounded font-bold ${
                                      pt.progress_percentage >= 80  ? 'bg-green-300 text-green-900' :
                                      pt.progress_percentage >= 50  ? 'bg-yellow-300 text-yellow-900' :
                                      pt.progress_percentage >= 20  ? 'bg-orange-300 text-orange-900' :
                                      'bg-red-300 text-red-900'
                                    }`}>
                                      {pt.progress_percentage.toFixed(0)}%
                                    </span>
                                    <span className="text-white opacity-20 text-xs mx-1">·</span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Row: Selesai */}
                        {leader.pts.filter(p => p.progress_percentage >= 100).length > 0 && (
                          <div className="flex items-center" style={{ minHeight: '30px' }}>
                            <div className="bg-white bg-opacity-10 px-3 self-stretch flex items-center flex-shrink-0">
                              <span className="text-green-200 text-xs font-semibold tracking-widest uppercase">Selesai</span>
                            </div>
                            <div className="w-px self-stretch bg-white bg-opacity-20 flex-shrink-0" />
                            <div className="overflow-hidden flex-1">
                              <div className="leader-ticker whitespace-nowrap inline-flex items-center"
                                style={{ animation: `leaderScroll ${Math.max(leader.pts.filter(p => p.progress_percentage >= 100).length * 5, 10)}s linear infinite` }}>
                                {[...leader.pts.filter(p => p.progress_percentage >= 100), ...leader.pts.filter(p => p.progress_percentage >= 100)].map((pt, i) => (
                                  <span key={i} className="inline-flex items-center gap-2 px-4">
                                    <span className="w-1.5 h-1.5 rounded-full bg-green-300 flex-shrink-0" />
                                    <span className="text-white text-xs font-medium">
                                      {pt.name.length > 30 ? pt.name.substring(0, 30) + '…' : pt.name}
                                    </span>
                                    <span className="text-xs px-1.5 py-0.5 rounded font-bold bg-green-300 text-green-900">
                                      100%
                                    </span>
                                    <span className="text-white opacity-20 text-xs mx-1">·</span>
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}

                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* PT Cards Grid */}
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pts.map((pt) => (
            <PTCard
              key={pt.id}
              pt={pt}
              onViewDetails={handleViewDetails}
              onViewComments={handleViewComments}
            />
          ))}
        </div>
      </div>

      {/* Detail Modal */}
      {selectedPT && (
        <PTDetailModal pt={selectedPT} onClose={handleCloseModal} />
      )}

      {/* Comments Modal */}
      {commentsPT && (
        <CommentsModal pt={commentsPT} onClose={handleCloseComments} />
      )}

      {/* Footer */}
      <div className="max-w-7xl mx-auto mt-8 text-center text-blue-200 text-sm">
        <p>Auditra Monitoring Dashboard v1.0 | Auto-refresh every 30 minutes</p>
        <p className="text-xs text-blue-300 mt-1">Press Ctrl+T to toggle TV Mode</p>
      </div>
      </div>

      {/* Marquee Animation Style */}
      <style>{`
        @keyframes marquee {
          0% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(-50%);
          }
        }
        .animate-marquee {
          animation: marquee 30s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
        @keyframes leaderScroll {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .leader-ticker {
          will-change: transform;
        }
        .leader-ticker:hover {
          animation-play-state: paused;
        }
      `}</style>
    </div>
  );
};

export default MonitoringDashboard;